import { ReactNode, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Collapse } from '@mui/material';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import { SectionState } from '../sectionState';

const statusChip = (section: SectionState) => {
  if (section.status === 'complete') {
    return {
      label:
        section.count === undefined
          ? getTrans('cvBuilderPage.summary.state.complete')
          : `${section.count}`,
      tone: 'success' as const
    };
  }
  if (section.blocking) {
    return {
      label: getTrans('cvBuilderPage.summary.state.required'),
      tone: 'error' as const
    };
  }
  if (section.status === 'partial') {
    return {
      label: getTrans('cvBuilderPage.summary.state.partial'),
      tone: 'warning' as const
    };
  }
  return {
    label: getTrans('cvBuilderPage.summary.state.empty'),
    tone: 'neutral' as const
  };
};

/**
 * One collapsed section of the review screen: name, state, and the content on demand.
 *
 * The Summary used to render every field of every section expanded — nine bordered
 * read-only boxes for personal details alone, then every education, experience and skill
 * entry — so the submit bar sat behind a very long scroll and nothing signalled which
 * sections still needed work. Collapsing by default with the state on the row is the
 * pattern the review screens we benchmarked use (HelloFresh, Grill'd), and it makes the
 * one thing the talent needs to notice — a missing required section — visible without
 * scrolling.
 */
export const SummarySectionRow = ({
  title,
  section,
  summaryLine,
  onEdit,
  children
}: {
  title: string;
  section: SectionState;
  /** One-line preview shown while collapsed (e.g. "Anna Schmidt · Berlin"). */
  summaryLine?: string;
  onEdit: () => void;
  children: ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  const chip = statusChip(section);

  return (
    <Box
      data-testid={`summary-section-${section.key}`}
      sx={theme => ({
        border: '1px solid',
        borderColor: section.blocking
          ? theme.palette.error.main
          : theme.palette.divider,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        mb: 1.5,
        overflow: 'hidden'
      })}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded(current => !current)}
        aria-expanded={expanded}
        // Stable RUM action name: replay masking would otherwise record every one of these
        // as "Masked Element", making summary dead/rage clicks impossible to group.
        data-dd-action-name={`cv-summary-toggle:${section.key}`}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          border: 0,
          background: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text variant="subtitle2" color="text.primary">
            {title}
          </Text>
          {summaryLine && (
            <Text
              variant="caption"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {summaryLine}
            </Text>
          )}
        </Box>

        <Chip
          size="small"
          label={chip.label}
          data-testid={`summary-state-${section.key}`}
          sx={theme => ({
            flex: 'none',
            height: 20,
            fontSize: '0.6875rem',
            ...(chip.tone === 'success' && {
              backgroundColor: theme.palette.success.lighter,
              color: theme.palette.success.dark
            }),
            ...(chip.tone === 'error' && {
              backgroundColor: theme.palette.error.lighter,
              color: theme.palette.error.dark
            }),
            ...(chip.tone === 'warning' && {
              backgroundColor: theme.palette.warning.lighter,
              color: theme.palette.warning.dark
            }),
            ...(chip.tone === 'neutral' && {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.secondary
            })
          })}
        />
        {expanded ? (
          <ExpandLessIcon sx={{ flex: 'none', color: 'text.secondary' }} />
        ) : (
          <ExpandMoreIcon sx={{ flex: 'none', color: 'text.secondary' }} />
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Box sx={{ mb: 2 }}>{children}</Box>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onEdit}
            fullWidth
            data-dd-action-name={`cv-summary-edit:${section.key}`}
          >
            {getTrans('general.edit')}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};
